class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.addEventListener('click', this.handleQuickAdd.bind(this));
    this.addEventListener('click', this.handleQuantityButtons.bind(this));
    this.addEventListener('change', this.handleQuantityInput.bind(this));

    this.notification.addEventListener(
      'keyup',
      (evt) => evt.code === 'Escape' && this.close()
    );

    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;

    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML =
        this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector
        );
    });

    if (this.header) this.header.reveal();

    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  handleQuantityButtons(event) {
    const minus = event.target.closest('.cart-notification-qty-minus');
    const plus = event.target.closest('.cart-notification-qty-plus');

    if (!minus && !plus) return;

    event.preventDefault();
    event.stopPropagation();

    const upsell = event.target.closest('.cart-notification-upsell');
    const input = upsell?.querySelector('.cart-notification-qty-input');

    if (!input) return;

    let quantity = parseInt(input.value, 10) || 1;

    if (plus) {
      quantity += 1;
    }

    if (minus) {
      quantity = Math.max(1, quantity - 1);
    }

    input.value = quantity;
  }

  handleQuantityInput(event) {
    const input = event.target.closest('.cart-notification-qty-input');

    if (!input) return;

    let quantity = parseInt(input.value, 10) || 1;

    if (quantity < 1) {
      quantity = 1;
    }

    input.value = quantity;
  }

  async handleQuickAdd(event) {
    const button = event.target.closest('.cart-notification-quick-add');

    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    if (button.disabled) return;

    const variantId = button.dataset.variantId;

    const upsell = button.closest('.cart-notification-upsell');

    const quantityInput = upsell?.querySelector(
      '.cart-notification-qty-input'
    );

    const quantity = Math.max(
      1,
      parseInt(quantityInput?.value, 10) || 1
    );

    const label = button.querySelector('span');
    const originalLabel = label ? label.textContent : '+ Add';

    const errorBox = upsell?.querySelector(
      '.cart-notification-upsell__error'
    );

    button.disabled = true;

    if (label) {
      label.textContent =
        button.dataset.addingText || 'Adding...';
    }

    if (errorBox) {
      errorBox.hidden = true;
      errorBox.textContent = '';
    }

    try {
      const response = await fetch(
        `${window.Shopify.routes.root}cart/add.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                id: Number(variantId),
                quantity: quantity,
              },
            ],
          }),
        }
      );

      const addedData = await response.json();

      if (!response.ok) {
        throw new Error(
          addedData.description ||
          addedData.message ||
          'Unable to add item.'
        );
      }

      const sectionIds = this.getSectionsToRender()
        .map((section) => section.id)
        .join(',');

      const sectionsResponse = await fetch(
        `${window.Shopify.routes.root}?sections=${sectionIds}`
      );

      if (!sectionsResponse.ok) {
        throw new Error('Unable to refresh cart.');
      }

      const sections = await sectionsResponse.json();

      const cartResponse = await fetch(
        `${window.Shopify.routes.root}cart.js`,
        {
          headers: {
            Accept: 'application/json',
          },
        }
      );

      const cart = await cartResponse.json();

      // Update cart icon bubble
      const cartIconSection = sections['cart-icon-bubble'];

      if (cartIconSection) {
        const parsed = new DOMParser().parseFromString(
          cartIconSection,
          'text/html'
        );

        const newBubble =
          parsed.querySelector('#cart-icon-bubble');

        const currentBubble =
          document.querySelector('#cart-icon-bubble');

        if (newBubble && currentBubble) {
          currentBubble.innerHTML = newBubble.innerHTML;
        }
      }

      // Update cart notification button/count area
      const buttonSection =
        sections['cart-notification-button'];

      if (buttonSection) {
        const parsed = new DOMParser().parseFromString(
          buttonSection,
          'text/html'
        );

        const newButton = parsed.querySelector(
          '#cart-notification-button'
        );

        const currentButton = document.querySelector(
          '#cart-notification-button'
        );

        if (newButton && currentButton) {
          currentButton.innerHTML = newButton.innerHTML;
        }
      }

      if (label) {
        label.textContent =
          button.dataset.addedText || '✓ Added';
      }

      button.disabled = true;

      if (quantityInput) {
        quantityInput.disabled = true;
      }

      const minusButton =
        upsell?.querySelector('.cart-notification-qty-minus');

      const plusButton =
        upsell?.querySelector('.cart-notification-qty-plus');

      if (minusButton) minusButton.disabled = true;
      if (plusButton) plusButton.disabled = true;

    } catch (error) {
      console.error('Quick add error:', error);

      button.disabled = false;

      if (label) {
        label.textContent = originalLabel;
      }

      if (errorBox) {
        errorBox.textContent =
          error.message ||
          'Unable to add item. Please try again.';

        errorBox.hidden = false;
      }
    }
  }

  handleBodyClick(evt) {
    const target = evt.target;

    if (
      target !== this.notification &&
      !target.closest('cart-notification')
    ) {
      const disclosure = target.closest(
        'details-disclosure, header-menu'
      );

      this.activeElement = disclosure
        ? disclosure.querySelector('summary')
        : null;

      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define(
  'cart-notification',
  CartNotification
);